import { browserSupportsWebAuthn, startRegistration } from '@simplewebauthn/browser';
import { AlertTriangle, CheckCircle, Fingerprint, Loader2, XCircle } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
	completeInviteSetup,
	getInviteRegistrationOptions,
	type InviteValidation,
	validateInvite,
} from '@/api/auth';
import { setAuthToken } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

type PageState = 'loading' | 'valid' | 'invalid' | 'registering' | 'success' | 'error';

interface InvitePageProps {
	token: string;
	onSuccess?: () => void;
	redirectTo?: string;
}

export function InvitePage({ token, onSuccess, redirectTo = '/' }: InvitePageProps) {
	const [pageState, setPageState] = useState<PageState>('loading');
	const [invite, setInvite] = useState<InviteValidation | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [deviceName, setDeviceName] = useState('');
	const [supportsWebAuthn, setSupportsWebAuthn] = useState(true);

	// Validate invite token on mount
	useEffect(() => {
		async function validate() {
			const result = await validateInvite(token);
			setInvite(result);

			if (result.valid) {
				setPageState('valid');
			} else {
				setPageState('invalid');
			}
		}

		// Check WebAuthn support
		const webAuthnSupported = browserSupportsWebAuthn();
		setSupportsWebAuthn(webAuthnSupported);

		validate();
	}, [token]);

	const handleRegister = useCallback(async () => {
		if (!invite?.valid) {
			setError('Invalid invite data');
			setPageState('error');
			return;
		}

		setPageState('registering');
		setError(null);

		try {
			// Step 1: Get registration options from server (using invite token)
			const { options, challenge, error: optionsError } = await getInviteRegistrationOptions(token);

			if (optionsError || !options || !challenge) {
				setError(optionsError || 'Failed to get registration options');
				setPageState('error');
				return;
			}

			// Step 2: Prompt user for passkey registration
			const credential = await startRegistration({ optionsJSON: options });

			// Step 3: Complete setup with the credential
			const result = await completeInviteSetup(
				token,
				credential,
				challenge,
				deviceName.trim() || undefined
			);

			if (result.success && result.token) {
				setAuthToken(result.token);
				setPageState('success');

				// Wait a moment to show success, then redirect
				setTimeout(() => {
					if (onSuccess) {
						onSuccess();
					} else {
						window.location.href = redirectTo;
					}
				}, 1500);
			} else {
				setError(result.error || 'Registration failed');
				setPageState('error');
			}
		} catch (err) {
			if (err instanceof Error) {
				if (err.name === 'NotAllowedError') {
					setError('Registration was cancelled or timed out');
				} else if (err.name === 'InvalidStateError') {
					setError('This passkey is already registered');
				} else if (err.name === 'NotSupportedError') {
					setError('This browser or device does not support passkeys');
				} else {
					setError(err.message);
				}
			} else {
				setError('An unexpected error occurred');
			}
			setPageState('error');
		}
	}, [invite, token, deviceName, onSuccess, redirectTo]);

	function getInvalidReason(): string {
		if (!invite) return 'Unable to validate invite';

		switch (invite.reason) {
			case 'not_found':
				return 'This invite link is invalid or does not exist.';
			case 'used':
				return 'This invite has already been used. Please contact an administrator if you need a new invite.';
			case 'expired':
				return 'This invite has expired. Please request a new invite from your administrator.';
			default:
				return 'This invite link is not valid.';
		}
	}

	function formatExpiresAt(timestamp: number): string {
		const date = new Date(timestamp * 1000);
		return date.toLocaleDateString(undefined, {
			month: 'long',
			day: 'numeric',
			year: 'numeric',
			hour: 'numeric',
			minute: '2-digit',
		});
	}

	// Loading state
	if (pageState === 'loading') {
		return (
			<div className="min-h-screen flex items-center justify-center p-4 bg-background">
				<Card className="w-full max-w-md">
					<CardContent className="flex flex-col items-center justify-center py-12">
						<Loader2 className="size-8 animate-spin text-muted-foreground" />
						<p className="mt-4 text-muted-foreground">Validating invite...</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Invalid invite state
	if (pageState === 'invalid') {
		return (
			<div className="min-h-screen flex items-center justify-center p-4 bg-background">
				<Card className="w-full max-w-md">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-destructive/10">
							<XCircle className="size-6 text-destructive" />
						</div>
						<CardTitle className="text-2xl font-bold">Invalid Invite</CardTitle>
						<CardDescription>{getInvalidReason()}</CardDescription>
					</CardHeader>
					<CardContent className="text-center">
						<Button variant="outline" onClick={() => (window.location.href = '/login')}>
							Go to Login
						</Button>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Success state
	if (pageState === 'success') {
		return (
			<div className="min-h-screen flex items-center justify-center p-4 bg-background">
				<Card className="w-full max-w-md">
					<CardContent className="flex flex-col items-center justify-center py-12">
						<div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-green-500/10">
							<CheckCircle className="size-6 text-green-600 dark:text-green-400" />
						</div>
						<h2 className="text-xl font-semibold">Account Setup Complete!</h2>
						<p className="mt-2 text-muted-foreground text-center">
							Redirecting you to the dashboard...
						</p>
					</CardContent>
				</Card>
			</div>
		);
	}

	// Valid invite - registration form
	return (
		<div className="min-h-screen flex items-center justify-center p-4 bg-background">
			<Card className="w-full max-w-md">
				<CardHeader className="text-center">
					<div className="mx-auto mb-4 flex size-12 items-center justify-center rounded-full bg-primary/10">
						<Fingerprint className="size-6 text-primary" />
					</div>
					<CardTitle className="text-2xl font-bold">Welcome!</CardTitle>
					<CardDescription>
						{invite?.display_name || invite?.username
							? `Setting up account for ${invite.display_name || invite.username}`
							: 'Complete your account setup by registering a passkey'}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-6">
					{/* User info */}
					{invite?.username && (
						<div className="rounded-lg border bg-muted/50 p-4">
							<div className="space-y-1 text-sm">
								<div className="flex justify-between">
									<span className="text-muted-foreground">Username</span>
									<span className="font-medium">{invite.username}</span>
								</div>
								{invite.expires_at && (
									<div className="flex justify-between">
										<span className="text-muted-foreground">Invite expires</span>
										<span className="font-medium">{formatExpiresAt(invite.expires_at)}</span>
									</div>
								)}
							</div>
						</div>
					)}

					{/* Error message */}
					{error && (
						<div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm flex items-start gap-2">
							<AlertTriangle className="size-4 mt-0.5 shrink-0" />
							<span>{error}</span>
						</div>
					)}

					{/* WebAuthn not supported warning */}
					{!supportsWebAuthn && (
						<div className="p-3 rounded-md bg-amber-500/10 text-amber-700 dark:text-amber-400 text-sm flex items-start gap-2">
							<AlertTriangle className="size-4 mt-0.5 shrink-0" />
							<div>
								<p className="font-medium">Browser not supported</p>
								<p className="mt-1">
									Your browser doesn't support passkeys. Please use a modern browser like Chrome,
									Firefox, Safari, or Edge to complete registration.
								</p>
							</div>
						</div>
					)}

					{/* Device name input (optional) */}
					<div className="space-y-2">
						<Label htmlFor="device-name">Device Name (optional)</Label>
						<Input
							id="device-name"
							type="text"
							placeholder="e.g., MacBook Pro, iPhone"
							value={deviceName}
							onChange={(e) => setDeviceName(e.target.value)}
							disabled={pageState === 'registering'}
							maxLength={100}
						/>
						<p className="text-xs text-muted-foreground">
							Give your passkey a name to help identify it later
						</p>
					</div>

					{/* Register button */}
					<Button
						className="w-full"
						size="lg"
						onClick={handleRegister}
						disabled={pageState === 'registering' || !supportsWebAuthn}
					>
						{pageState === 'registering' ? (
							<Loader2 className="size-5 animate-spin" />
						) : (
							<Fingerprint className="size-5" />
						)}
						{pageState === 'registering' ? 'Registering...' : 'Register with Passkey'}
					</Button>

					{/* Retry button after error */}
					{pageState === 'error' && (
						<Button
							variant="outline"
							className="w-full"
							onClick={() => {
								setPageState('valid');
								setError(null);
							}}
						>
							Try Again
						</Button>
					)}

					{/* Info about passkeys */}
					<div className="text-center">
						<p className="text-xs text-muted-foreground">
							Passkeys use your device's biometrics (like Face ID or fingerprint) for secure,
							passwordless authentication.
						</p>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

export default InvitePage;
